import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User as SupaUser, Session } from "@supabase/supabase-js";
import {
  clearPendingMeasures,
  loadPendingMeasures,
  savePendingMeasures,
} from "@/lib/pendingMeasures";
import {
  clearPendingDietPreferences,
  loadPendingDietPreferences,
  savePendingDietPreferences,
} from "@/lib/pendingDietCheckout";

// Helper to get the correct redirect URL based on environment
const getRedirectUrl = (path: string = ""): string => {
  const origin = window.location.origin;
  const envUrl = (import.meta.env.VITE_APP_URL as string | undefined)?.trim();

  // Only the internal Lovable live preview should redirect back to itself.
  // The published lovable.app subdomain and the custom domain must use VITE_APP_URL.
  const isPreview = origin.includes("id-preview--");
  const isLocalhost = origin.includes("localhost") || origin.includes("127.0.0.1");

  // Priority:
  // 1) Preview should redirect back to preview
  // 2) If VITE_APP_URL is set, use it everywhere else (prevents accidental localhost redirects)
  // 3) Fallback to current origin
  const baseUrl = (isPreview ? origin : (envUrl || origin)).replace(/\/$/, "");

  const cleanPath =
    path && (path.startsWith("/") || path.startsWith("?") || path.startsWith("#"))
      ? path
      : path
        ? `/${path}`
        : "";

  // If somehow VITE_APP_URL isn't set and we're on localhost, keep behavior deterministic
  // by not generating malformed URLs.
  if (!envUrl && isLocalhost && !isPreview) {
    return `${origin}${cleanPath}`;
  }

  return `${baseUrl}${cleanPath}`;
};

interface User {
  id: string;
  email: string;
  name: string;
  phone?: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ error?: string }>;
  register: (name: string, email: string, phone: string, password: string) => Promise<{ error?: string }>;
  logout: () => Promise<void>;
  updateName: (name: string) => Promise<{ error?: string }>;
  requestEmailChangeLink: () => Promise<{ error?: string }>;
  updateEmail: (newEmail: string) => Promise<{ error?: string }>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be within AuthProvider");
  return ctx;
};

const mapUser = (su: SupaUser): User => ({
  id: su.id,
  email: su.email ?? "",
  name: su.user_metadata?.name ?? su.email?.split("@")[0] ?? "",
  phone: su.user_metadata?.phone ?? "",
});

// Restore pre-registration diet data (measures + food preferences) that was
// attached to the account metadata during signUp. This fixes the case where the
// email confirmation link opens in a new tab and the local session storage is
// empty. Runs at most once per account, then clears the metadata.
const restorePendingFromMetadata = (su: SupaUser) => {
  const meta = (su.user_metadata ?? {}) as Record<string, unknown>;
  const pendingMeasures = meta.pending_measures as unknown;
  const pendingPrefs = meta.pending_diet_preferences as unknown;

  let restored = false;
  try {
    if (pendingMeasures && !loadPendingMeasures()) {
      savePendingMeasures(pendingMeasures as never);
      restored = true;
    }
    if (pendingPrefs && !loadPendingDietPreferences()) {
      savePendingDietPreferences(pendingPrefs as never);
      restored = true;
    }
  } catch (err) {
    console.warn("[Auth] Failed to restore pending diet data:", err);
  }

  if (restored || pendingMeasures || pendingPrefs) {
    // Clear metadata so we don't restore again on future logins
    supabase.auth
      .updateUser({ data: { pending_measures: null, pending_diet_preferences: null } })
      .catch((err) => console.warn("[Auth] Failed to clear pending metadata:", err));
  }
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;

    // Safety timeout: if auth never resolves after 8s, force loading=false
    const safetyTimeout = setTimeout(() => {
      if (mounted.current && loading) {
        console.warn("[Auth] Safety timeout — forcing loading=false after 8s");
        setLoading(false);
      }
    }, 8000);

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, sess) => {
      if (!mounted.current) return;
      setSession(sess);
      setUser(sess?.user ? mapUser(sess.user) : null);
      setLoading(false);
      // Restore any pre-registration diet data attached to the account metadata
      if (sess?.user) restorePendingFromMetadata(sess.user);
    });

    supabase.auth.getSession().then(({ data: { session: sess } }) => {
      if (!mounted.current) return;
      const hasAuthHash =
        window.location.hash.includes("access_token=") ||
        window.location.hash.includes("type=") ||
        window.location.hash.includes("refresh_token=");

      // Avoid race condition on auth-link redirects
      if (!sess && hasAuthHash) return;

      setSession(sess);
      setUser(sess?.user ? mapUser(sess.user) : null);
      setLoading(false);
    }).catch((err) => {
      console.error("[Auth] getSession failed:", err);
      if (mounted.current) setLoading(false);
    });

    // When the tab becomes visible again, the SDK's autoRefreshToken handles
    // refreshing automatically. We avoid calling getSession()/refreshSession()
    // manually here because that competes with onAuthStateChange and other
    // in-flight requests for the auth lock, causing
    // "Lock ... was released because another request stole it" errors.
    const handleVisibilityChange = () => {
      // Intentionally a no-op. Kept as a hook in case we need lightweight
      // in-memory revalidation in the future (without touching auth lock).
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      mounted.current = false;
      clearTimeout(safetyTimeout);
      subscription.unsubscribe();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const translateError = (msg: string): string => {
    const map: Record<string, string> = {
      "Invalid login credentials": "Email ou senha incorretos",
      "Email not confirmed": "Email ainda não confirmado. Verifique sua caixa de entrada.",
      "User already registered": "Este email já está cadastrado",
      "A user with this email address has already been registered": "Este email já está cadastrado. Tente fazer login.",
      "Password should be at least 6 characters": "A senha deve ter no mínimo 6 caracteres",
      "Signup requires a valid password": "É necessário informar uma senha válida",
      "Unable to validate email address: invalid format": "Formato de email inválido",
      "Email rate limit exceeded": "Muitas tentativas. Aguarde alguns minutos e tente novamente.",
      "For security purposes, you can only request this once every 60 seconds": "Por segurança, aguarde 60 segundos antes de tentar novamente.",
      "To signup, please provide your email": "Informe seu email para se cadastrar.",
      "Anonymous sign-ins are disabled": "Cadastro anônimo não permitido.",
      "Database error saving new user": "Erro ao salvar cadastro. Tente novamente mais tarde.",
      "User not found": "Usuário não encontrado.",
      "New password should be different from the old password.": "A nova senha deve ser diferente da senha atual.",
      "Auth session missing!": "Sessão expirada. Faça login novamente.",
      "Password recovery requires an email": "Informe seu email para recuperar a senha.",
      "over_email_send_rate_limit": "Muitos emails enviados. Aguarde alguns minutos.",
      "Load failed": "Falha na conexão. Verifique sua internet e tente novamente.",
      "Failed to fetch": "Falha na conexão. Verifique sua internet e tente novamente.",
      "NetworkError when attempting to fetch resource.": "Falha na conexão. Verifique sua internet e tente novamente.",
      "Password is known to be weak and easy to guess, please choose a different one.": "Esta senha é muito comum e fácil de adivinhar. Escolha uma senha diferente e mais forte.",
      "Password is known to be weak and easy to guess, please choose a different one": "Esta senha é muito comum e fácil de adivinhar. Escolha uma senha diferente e mais forte.",
    };
    if (map[msg]) return map[msg];
    const lower = msg.toLowerCase();
    if (lower.includes("password is known to be weak") || lower.includes("weak_password") || lower.includes("pwned")) {
      return "Esta senha é muito comum ou apareceu em vazamentos. Escolha uma senha diferente e mais forte.";
    }
    const minMatch = msg.match(/Password should be at least (\d+) characters/i);
    if (minMatch) return `A senha deve ter no mínimo ${minMatch[1]} caracteres.`;
    return msg;
  };

  const login = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: translateError(error.message) };
    return {};
  };

  const register = async (name: string, email: string, phone: string, password: string) => {
    // Capture any diet data the user built before creating the account so it can
    // be restored even if the email confirmation opens in a new tab/device.
    const pendingMeasures = loadPendingMeasures();
    const pendingPrefs = loadPendingDietPreferences();
    const hasPendingDiet = !!pendingMeasures || !!pendingPrefs;

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: getRedirectUrl(hasPendingDiet ? "/login?next=/diet-builder" : "/login"),
        data: {
          name,
          phone,
          ...(pendingMeasures ? { pending_measures: pendingMeasures } : {}),
          ...(pendingPrefs ? { pending_diet_preferences: pendingPrefs } : {}),
        },
      },
    });
    if (error) return { error: translateError(error.message) };
    return {};
  };

  const updateName = async (name: string) => {
    const { error } = await supabase.auth.updateUser({ data: { name } });
    if (error) return { error: translateError(error.message) };

    const { data: { user: updatedUser } } = await supabase.auth.getUser();
    if (updatedUser) {
      setUser(mapUser(updatedUser));
      await supabase.from("profiles").update({ name }).eq("user_id", updatedUser.id);
    }
    return {};
  };

  const requestEmailChangeLink = async () => {
    if (!user?.email) return { error: "Usuário sem email válido" };

    const { error } = await supabase.auth.signInWithOtp({
      email: user.email,
      options: {
        shouldCreateUser: false,
        emailRedirectTo: getRedirectUrl("/profile?email_change_auth=1"),
      },
    });

    if (error) return { error: translateError(error.message) };
    return {};
  };

  const updateEmail = async (newEmail: string) => {
    const { error } = await supabase.auth.updateUser(
      { email: newEmail },
      { emailRedirectTo: getRedirectUrl("/profile") }
    );
    if (error) return { error: translateError(error.message) };
    return {};
  };

  const refreshUser = async () => {
    const { data: { user: u } } = await supabase.auth.getUser();
    if (u) setUser(mapUser(u));
  };

  const logout = async () => {
    const clearPersistedAppState = () => {
      clearPendingMeasures();
      clearPendingDietPreferences();
      sessionStorage.removeItem("email_change_authorized_until");
      sessionStorage.removeItem("expired_plan_dismissed");
    };

    try {
      clearPersistedAppState();
      await supabase.auth.signOut();
    } finally {
      setUser(null);
      setSession(null);
      // Do not keep any app/user data in localStorage after logout
      localStorage.clear();
      // Remove possible Supabase leftovers from sessionStorage as well
      Object.keys(sessionStorage)
        .filter((k) => k === "supabase.auth.token" || k.startsWith("sb-"))
        .forEach((k) => sessionStorage.removeItem(k));
      clearPersistedAppState();
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, login, register, logout, updateName, requestEmailChangeLink, updateEmail, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
